import type { ReactNode } from "react";

import { cn } from "~/lib/cn";
import type { JsonValue } from "~/lib/shared/json";
import { resolveLinkAttrs } from "~/lib/shared/link-rel";

/**
 * Server-side renderer for Tiptap JSON article bodies. Walks the JSON tree
 * and maps each node type to a React element — no Tiptap editor mount, no
 * useEditor, no client-only effects, so the article body is part of the
 * server-rendered HTML. New custom Tiptap nodes are supported by adding a
 * case to `renderNode` / `renderMark` below.
 *
 * Link marks go through the platform link policy (`resolveLinkAttrs`). Pass
 * `ugc` for coach-authored bodies (e.g. coaching articles) so their outbound
 * links carry `rel="ugc"`; leave it off for editorial bodies (CIH frameworks).
 *
 * Aliases: tiptap renderer, article body walker, prosemirror json renderer
 */

interface ArticleBodyProps {
  body: JsonValue | null | undefined;
  className?: string;
  /** Mark outbound links as user-generated (coach-authored). Default editorial. */
  ugc?: boolean;
}

interface JsonNode {
  type?: string;
  content?: JsonNode[];
  marks?: Array<{ type?: string; attrs?: Record<string, unknown> }>;
  text?: string;
  attrs?: Record<string, unknown>;
}

function isJsonNode(value: unknown): value is JsonNode {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function getString(
  attrs: Record<string, unknown> | undefined,
  key: string,
): string | undefined {
  const v = attrs?.[key];
  return typeof v === "string" ? v : undefined;
}

function getNumber(
  attrs: Record<string, unknown> | undefined,
  key: string,
): number | undefined {
  const v = attrs?.[key];
  return typeof v === "number" ? v : undefined;
}

export function ArticleBody({
  body,
  className,
  ugc = false,
}: ArticleBodyProps) {
  if (!isJsonNode(body)) return null;
  if (!Array.isArray(body.content) || body.content.length === 0) return null;
  return (
    <div className={cn(className)}>
      {body.content.map((child, i) => (
        <NodeView key={i} node={child} ugc={ugc} />
      ))}
    </div>
  );
}

function NodeView({ node, ugc }: { node: JsonNode; ugc: boolean }): ReactNode {
  return renderNode(node, ugc);
}

function renderChildren(
  nodes: JsonNode[] | undefined,
  ugc: boolean,
): ReactNode {
  if (!nodes) return null;
  return nodes.map((child, i) => <NodeView key={i} node={child} ugc={ugc} />);
}

function renderNode(node: JsonNode, ugc: boolean): ReactNode {
  if (!isJsonNode(node)) return null;
  switch (node.type) {
    case "text":
      return renderText(node, ugc);
    case "paragraph":
      return <p>{renderChildren(node.content, ugc)}</p>;
    case "heading": {
      const raw = getNumber(node.attrs, "level") ?? 1;
      const level = Math.max(1, Math.min(6, raw));
      const Tag = `h${level}` as "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
      // Forward `id` when present so callers that pre-compute heading
      // anchors (see `annotateBodyHeadings` in `~/lib/shared/cih/body-toc`)
      // can wire in-page nav and have the targets land in SSR HTML.
      const id = getString(node.attrs, "id");
      return <Tag id={id}>{renderChildren(node.content, ugc)}</Tag>;
    }
    case "bulletList":
      return <ul>{renderChildren(node.content, ugc)}</ul>;
    case "orderedList": {
      const start = getNumber(node.attrs, "start");
      return <ol start={start}>{renderChildren(node.content, ugc)}</ol>;
    }
    case "listItem":
      return <li>{renderChildren(node.content, ugc)}</li>;
    case "blockquote":
      return <blockquote>{renderChildren(node.content, ugc)}</blockquote>;
    case "codeBlock": {
      const language = getString(node.attrs, "language");
      return (
        <pre>
          <code className={language ? `language-${language}` : undefined}>
            {renderChildren(node.content, ugc)}
          </code>
        </pre>
      );
    }
    case "horizontalRule":
      return <hr />;
    case "hardBreak":
      return <br />;
    case "table":
      // The wrapping div lets wide tables scroll horizontally on narrow
      // viewports rather than overflowing the column.
      return (
        <div className="overflow-x-auto">
          <table>
            <tbody>{renderChildren(node.content, ugc)}</tbody>
          </table>
        </div>
      );
    case "tableRow":
      return <tr>{renderChildren(node.content, ugc)}</tr>;
    case "tableHeader":
      return <th {...cellAttrs(node)}>{renderChildren(node.content, ugc)}</th>;
    case "tableCell":
      return <td {...cellAttrs(node)}>{renderChildren(node.content, ugc)}</td>;
    case "image": {
      const rawSrc = getString(node.attrs, "src");
      const src = rawSrc ? sanitizeUrl(rawSrc) : null;
      if (!src) return null;
      const alt = getString(node.attrs, "alt") ?? "";
      const title = getString(node.attrs, "title");
      return (
        <img
          src={src}
          alt={alt}
          title={title}
          loading="lazy"
          decoding="async"
        />
      );
    }
    default:
      // Unknown node type — render children inline so we never drop user
      // text on the floor, but skip the wrapper.
      return <>{renderChildren(node.content, ugc)}</>;
  }
}

function cellAttrs(node: JsonNode): {
  colSpan?: number;
  rowSpan?: number;
  style?: { textAlign: "left" | "right" | "center" };
} {
  const out: ReturnType<typeof cellAttrs> = {};
  const colspan = getNumber(node.attrs, "colspan");
  const rowspan = getNumber(node.attrs, "rowspan");
  const align = getString(node.attrs, "align");
  if (colspan !== undefined && colspan > 1) out.colSpan = colspan;
  if (rowspan !== undefined && rowspan > 1) out.rowSpan = rowspan;
  if (align === "left" || align === "right" || align === "center") {
    out.style = { textAlign: align };
  }
  return out;
}

function renderText(node: JsonNode, ugc: boolean): ReactNode {
  const text = node.text ?? "";
  if (!Array.isArray(node.marks) || node.marks.length === 0) return text;
  // Wrap inside out: the first mark in the list becomes the outermost
  // element. This matches how Tiptap stores marks (outermost first).
  return node.marks.reduceRight<ReactNode>(
    (children, mark) => renderMark(mark, children, ugc),
    text,
  );
}

function renderMark(
  mark: { type?: string; attrs?: Record<string, unknown> },
  children: ReactNode,
  ugc: boolean,
): ReactNode {
  switch (mark.type) {
    case "bold":
      return <strong>{children}</strong>;
    case "italic":
      return <em>{children}</em>;
    case "code":
      return <code>{children}</code>;
    case "strike":
      return <s>{children}</s>;
    case "underline":
      return <u>{children}</u>;
    case "link": {
      const rawHref = getString(mark.attrs, "href");
      const href = rawHref ? sanitizeUrl(rawHref) : null;
      if (!href) return <>{children}</>;
      const title = getString(mark.attrs, "title");
      return (
        <a href={href} title={title} {...resolveLinkAttrs(href, { ugc })}>
          {children}
        </a>
      );
    }
    default:
      return <>{children}</>;
  }
}

// Allow http(s), mailto, tel, sms, and relative targets (path, query, or
// fragment) — the same targets `normalizeLinkHref` in rich-text-editor.tsx
// accepts from the editor. Block javascript:, data:, vbscript:, etc. Used
// for both link hrefs and image srcs — even though browsers reject
// `javascript:` in <img src>, applying the same allowlist keeps the
// security posture uniform and protects against future attribute-based
// escape vectors.
function sanitizeUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (/^(https?:|mailto:|tel:|sms:)/i.test(trimmed)) return trimmed;
  if (/^[/?#]/.test(trimmed)) return trimmed;
  return null;
}
